import { useEffect } from 'react';

const FOCUSABLE = 'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';

/**
 * Focus contract borrowed from IdleWarningModal: move focus into the card on
 * open, trap Tab / Shift+Tab inside it, run onEscape on Escape (capture
 * phase, so page-level shortcuts never see keys typed at the modal), and
 * restore focus to `returnTo` (or the previously focused element) on close.
 */
export function useModalFocus({ cardRef, initialRef, onEscape, returnTo }) {
  useEffect(() => {
    const previous = typeof document !== 'undefined' ? document.activeElement : null;
    const initial = (initialRef && initialRef.current) || cardRef.current;
    if (initial && typeof initial.focus === 'function') {
      try { initial.focus(); } catch (e) { /* non-fatal */ }
    }
    return () => {
      const target = (returnTo && (typeof returnTo === 'function' ? returnTo() : returnTo)) || previous;
      if (target && document.contains(target) && typeof target.focus === 'function') {
        try { target.focus(); } catch (e) { /* non-fatal */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (onEscape) onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = cardRef.current ? cardRef.current.querySelectorAll(FOCUSABLE) : null;
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [cardRef, onEscape]);
}
